import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { TierProFilled } from '@heidi/design-system/icons';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { cn, getFaviconUrl, prefetchFavicons, useIsMobile } from '@heidi/utils';
import { ChevronDown } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Source } from '../../contracts';
import { deduplicateSources } from '../../contracts';
import {
  buildViewerSourceFromSearchSource,
  getSearchSourceSubtitle,
  openSearchBlockSource,
} from './openSearchBlockSource';
import { useEvidenceAnswerPorts } from './ports';

interface CollapsedSourcesSummaryProps {
  /** All sources from all sub-agent searches (will be de-duplicated) */
  sources: Source[];
  className?: string;
  /**
   * EVI-1486 analytics context. Threaded from `ChatTimeline` so each click on
   * a row in the aggregated rollup emits a fully-joinable
   * `evidence_source_clicked` event with `surface_block:
   * 'collapsed_sources_summary'`.
   */
  chatId?: string;
  messageId?: string;
  sessionId?: string | null;
}

/**
 * Single source item in expanded list - memoized to prevent unnecessary re-renders.
 *
 * Routes user-library sources (`source.type === 'user_document'`) into the
 * in-app `documentPreviewAtom` instead of `window.open(source.url)` — library
 * docs have no real file URL (the `url` field is a session deep-link), so an
 * anchor-based open-in-new-tab navigates the user to a broken session page.
 * Web/integration-partner sources open in the in-app source viewer when the
 * URL requires authenticated fetch or inline HTML rendering (e.g. MIMS proxy
 * paths) — matching citation badge behaviour. Ordinary web URLs still open
 * in a new tab on mobile; desktop uses the viewer by default.
 *
 * Per the EVI-1486 schema, `search_block_query` is intentionally undefined for
 * this surface — the rollup is a cross-sub-agent dedup, so no single
 * sub-agent query applies.
 */
type SourceItemProps = {
  source: Source;
  citationNumber: number;
  chatId?: string;
  messageId?: string;
  sessionId?: string | null;
};

const SourceItem = memo(
  ({ source, citationNumber, chatId, messageId, sessionId }: SourceItemProps) => {
    const {
      openInViewer,
      openLibraryDocument,
      openInNewTab,

      isAuthenticatedUrl,
    } = useEvidenceAnswerPorts();
    const isMobile = useIsMobile();

    const subtitle = getSearchSourceSubtitle(source);
    const title = source.title || subtitle;
    const showSubtitle = Boolean(source.title && subtitle && title !== subtitle);

    const handleClick = useCallback(() => {
      openSearchBlockSource(
        {
          source,
          isMobile,
          isAuthenticatedUrl,
          citationNumber,
          surfaceBlock: 'collapsed_sources_summary',
          chatId,
          messageId,
          sessionId,
        },
        {
          openLibraryDocument,
          openInViewer: (src) => openInViewer(buildViewerSourceFromSearchSource(src)),
          openInNewTab,
        }
      );
    }, [
      openLibraryDocument,
      openInViewer,
      openInNewTab,
      isAuthenticatedUrl,
      source,
      citationNumber,
      chatId,
      messageId,
      sessionId,
      isMobile,
    ]);

    return (
      <button
        type="button"
        onClick={handleClick}
        className="flex w-full min-w-0 items-center gap-2 rounded px-2 py-1.5 text-left transition-colors hover:bg-surface-hover"
      >
        <img
          src={getFaviconUrl(source.url)}
          alt=""
          className="size-4 shrink-0 rounded-sm"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        <span className="w-0 flex-1 truncate text-sm text-text-primary">{title}</span>
        {showSubtitle && (
          <TypographyCaption className="min-w-0 truncate text-text-tertiary">
            {subtitle}
          </TypographyCaption>
        )}
      </button>
    );
  }
);

SourceItem.displayName = 'SourceItem';

/**
 * Collapsed Sources Summary Component
 *
 * Displays a consolidated view of all sources from multiple sub-agent searches.
 * De-duplicates sources by URL and shows total count.
 *
 * Collapsed state: "⚡ 27 sources found [View sources >]"
 * Expanded state: Shows flat list of all unique sources
 */
export const CollapsedSourcesSummary = ({
  sources: rawSources,
  className,
  chatId,
  messageId,
  sessionId,
}: CollapsedSourcesSummaryProps): JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Memoize de-duplication to avoid recalculating on every render
  const uniqueSources = useMemo(() => deduplicateSources(rawSources), [rawSources]);
  const sourceCount = uniqueSources.length;

  const scrollState = useVerticalScrollFade(scrollRef, isExpanded, [uniqueSources]);

  // Memoize URLs for favicon prefetch to avoid recalculating
  const sourceUrls = useMemo(() => uniqueSources.map((s) => s.url), [uniqueSources]);

  // Prefetch favicons when sources arrive to prevent flicker
  useEffect(() => {
    if (sourceUrls.length > 0) {
      prefetchFavicons(sourceUrls);
    }
  }, [sourceUrls]);

  // Don't render if no sources
  if (sourceCount === 0) {
    return null;
  }

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      data-testid="collapsed-sources-summary"
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-xl bg-background-tertiary p-2 duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Header - always visible */}
      <CollapsibleTrigger asChild>
        <button className="flex w-full min-w-0 items-center justify-between overflow-hidden rounded-md p-2 text-left transition-colors hover:bg-surface-hover">
          {/* Left side: Icon + Count */}
          <div className="flex items-center gap-2">
            <TierProFilled size={18} className="shrink-0 text-tier-pro-text" />
            <TypographyCaptionBold className="text-text-secondary">
              <FormattedMessage
                defaultMessage="{count, plural, one {# source found} other {# sources found}}"
                id="eHeXeG2BFK"
                values={{ count: sourceCount }}
                description="Count of unique sources found across evidence searches"
              />
            </TypographyCaptionBold>
          </div>

          {/* Right side: View sources + chevron (text hidden on narrow screens) */}
          <div className="flex shrink-0 items-center gap-2">
            <TypographyCaption className="hidden text-text-secondary sm:block">
              <FormattedMessage
                defaultMessage="View sources"
                id="yknxzoy+hX"
                description="Expands the collapsed list of evidence sources"
              />
            </TypographyCaption>
            <ChevronDown
              className={cn(
                'size-4 text-text-secondary transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          </div>
        </button>
      </CollapsibleTrigger>

      {/* Expanded source list */}
      <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <div className="relative px-2 pb-2 pt-1">
          {/* Top fade gradient */}
          <div
            className={cn(
              'pointer-events-none absolute inset-x-2 top-1 z-10 h-6 bg-gradient-to-b from-background-tertiary to-transparent transition-opacity duration-200',
              scrollState.canScrollUp ? 'opacity-100' : 'opacity-0'
            )}
          />

          <ScrollArea
            scrollbarVariant="minimal"
            className="max-h-64"
            viewportRef={scrollRef}
            viewportClassName="max-h-64"
          >
            <div className="space-y-0.5">
              {uniqueSources.map((source, idx) => (
                <SourceItem
                  key={`source-${idx}-${source.url}`}
                  source={source}
                  citationNumber={idx + 1}
                  chatId={chatId}
                  messageId={messageId}
                  sessionId={sessionId}
                />
              ))}
            </div>
          </ScrollArea>

          {/* Bottom fade gradient */}
          <div
            className={cn(
              'pointer-events-none absolute inset-x-2 bottom-2 z-10 h-6 bg-gradient-to-t from-background-tertiary to-transparent transition-opacity duration-200',
              scrollState.canScrollDown ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
